import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PreloadAllModules, provideRouter, withPreloading } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { EffectsModule } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { tap } from 'rxjs';
import { routes } from '@app/app.routes';
import { cmsProviders } from '@b2b/components/cms/cms.providers';
import { AuthInterceptor } from '@core/authentication/interceptors/auth.interceptor';
import { dbConfig } from '@asm/features/sync/database/db.config';
import { CmsModule } from '@app/b2b/core/cms/cms.module';
import { UserStoreModule } from '@core/user/store/user.store.module';
import { userProviders } from '@core/user/user.providers';
import { AgentStoreModule } from '@asm/features/asm/store/agent.store.module';
import { CartStoreModule } from '@b2b/features/cart/core/store/cart.store.module';
import { termsModalProviders } from '@b2b/features/misc/terms-modal/terms-modal.providers';
import { b2bUnitProviders } from '@b2b/features/b2b-unit/core/b2b-unit.providers';
import { B2bUnitStoreModule } from '@b2b/features/b2b-unit/core/store/b2b-unit.store.module';
import { ProductStoreModule } from '@b2b/features/product/core/store/product.store.module';
import { LANGUAGES } from '@shared/i18n/languages';
import { ContextHeadersInterceptor } from '@core/interceptors/context-headers.interceptor';
import { OfflineInterceptor } from '@asm/features/sync/interceptor/offline.interceptor';
import { OrderStoreModule } from '@b2b/features/order/core/store/order.store.module';
import { agentProviders } from '@asm/features/asm/agent.providers';
import { storeProviders } from '@b2b/features/misc/store/store.providers';
import { AppFeaturesService } from '@core/services/app-features.service';
import { StoreService } from '@b2b/features/misc/store/store.service';

registerLocaleData(localePt, 'pt-BR');

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      const storeService = inject(StoreService);
      const appFeaturesService = inject(AppFeaturesService);

      return storeService
        .getConfig()
        .pipe(tap(config => appFeaturesService.initialize(config ?? storeService.getStoreFromCache())));
    }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideRouterStore(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(
      NgxIndexedDBModule.forRoot(dbConfig),
      TranslateModule.forRoot({
        defaultLanguage: LANGUAGES.PT_BR,
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    importProvidersFrom(
      StoreModule.forRoot(
        {
          router: routerReducer,
        },
        {},
      ),
      EffectsModule.forRoot([]),
      ProductStoreModule,
      CartStoreModule,
      UserStoreModule,
      AgentStoreModule,
      B2bUnitStoreModule,
      CmsModule,
      OrderStoreModule,
      StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() }),
    ),
    cmsProviders,
    userProviders,
    b2bUnitProviders,
    agentProviders,
    storeProviders,
    termsModalProviders,
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ContextHeadersInterceptor, multi: true },
  ],
};
